import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { usePageContext } from '@/hooks/useRouter';

const INVITE_CODE_KEY = 'inviteCode';
const INVITE_CODE_EXPIRY_KEY = 'inviteCodeExpiry';
const EXPIRY_DAYS = 30; // 30天过期

interface InviteCodeContextType {
  inviteCode: string | null;
  setInviteCode: (code: string | null) => void;
  clearInviteCode: () => void;
  isFromUrl: boolean;
}

const InviteCodeContext = createContext<InviteCodeContextType | undefined>(undefined);

export const InviteCodeProvider = ({ children }: { children: ReactNode }) => {
  const pageContext = usePageContext();
  const [isFromUrl, setIsFromUrl] = useState(false);

  // 初始化：从 localStorage 读取（检查过期）
  const [inviteCode, setInviteCodeState] = useState<string | null>(() => {
    // SSR 环境没有 localStorage
    if (typeof window === 'undefined') return null;

    const stored = localStorage.getItem(INVITE_CODE_KEY);
    const expiry = localStorage.getItem(INVITE_CODE_EXPIRY_KEY);

    if (stored && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        return stored;
      } else {
        // 过期了，清除
        localStorage.removeItem(INVITE_CODE_KEY);
        localStorage.removeItem(INVITE_CODE_EXPIRY_KEY);
      }
    }
    return null;
  });

  // 监听 URL 变化，自动提取邀请码（使用 Vike pageContext）
  useEffect(() => {
    // 仅在客户端执行
    if (typeof window === 'undefined') return;

    // 从 Vike pageContext 获取 URL 参数
    const searchParams = (pageContext as any).urlParsed?.search;
    const codeFromUrl = searchParams?.inviteCode;

    if (codeFromUrl && typeof codeFromUrl === 'string' && codeFromUrl.trim()) {
      // URL 带邀请码，更新存储
      const trimmedCode = codeFromUrl.trim();
      setInviteCode(trimmedCode);
      setIsFromUrl(true);
    } else {
      setIsFromUrl(false);
    }
  }, [pageContext.urlPathname, (pageContext as any).urlParsed?.search]);

  const setInviteCode = (code: string | null) => {
    if (code && code.trim()) {
      const trimmedCode = code.trim();
      const expiryTime = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      if (typeof window !== 'undefined') {
        localStorage.setItem(INVITE_CODE_KEY, trimmedCode);
        localStorage.setItem(INVITE_CODE_EXPIRY_KEY, expiryTime.toString());
      }
      setInviteCodeState(trimmedCode);
    } else {
      clearInviteCode();
    }
  };

  const clearInviteCode = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(INVITE_CODE_KEY);
      localStorage.removeItem(INVITE_CODE_EXPIRY_KEY);
    }
    setInviteCodeState(null);
    setIsFromUrl(false);
  };

  return (
    <InviteCodeContext.Provider value={{ inviteCode, setInviteCode, clearInviteCode, isFromUrl }}>
      {children}
    </InviteCodeContext.Provider>
  );
};

export const useInviteCode = () => {
  const context = useContext(InviteCodeContext);
  if (!context) {
    throw new Error('useInviteCode must be used within InviteCodeProvider');
  }
  return context;
};
